import 'ol/ol.css';
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import { Divider, Flex } from 'antd';
import { useParams, Link } from 'react-router';
import { Spinner } from 'react-bootstrap';

import MapView from './components/Map';
import BaseMapSelector from './components/layers/baseMap';
import MapScaleBar from '@components/mapScaleBar/MapScaleBar';
import { MapControlsCluster } from './components/mapControls';
import Dialogue from '@components/dialogue/Dialogue';
import LayersSideBar from './components/sidebars/LayerView';
import MapToolbar from './components/toolBar/MapToolbar';
import ModelPicker from './components/toolBar/modelPicker/ModelPicker';
import ObjectPicker from './components/utilityButtons/ObjectPicker';
import AnnotateObjects from './components/utilityButtons/AnnotateObjects';
import Annotate from './components/utilityButtons/Annotate';
import DetectInArea from './components/utilityButtons/DetectInArea';
import EditDetections from './components/utilityButtons/EditDetections';
import ConfirmModal from '@components/modal/ConfirmModal';
import ObjectPickerModal from '../../../../components/modal/objectPickerModal/ObjectPickerModal';
import ProjectTour from './components/ProjectTour';
import { TOUR_ID } from '@components/tour/tours';
import AoiPreviewLayer from '../../../../components/modal/objectPickerModal/AoiPreviewLayer';

import { useProject } from '@contexts/Project.context';
import { useProjectMode } from '@contexts/ProjectMode.context';
import { useMapToolBusy } from '@hooks/useMapToolBusy';
import { ANNOTATION_TARGET, PROJECT_MODE, PROJECT_VIEW_MODE } from '@utils/constants';
import { useAuth } from '@contexts/User.context';
import { getProject, buildTileUrl } from '@utils/api';
import { getLayerByCustomId } from '@utils/map/helpers';
import AnnotationSidebar from './components/sidebars/AnnotationSidebar';
import MapLegend from './components/MapLegend';
import CHMColorBar from './components/CHMColorBar';
// import TreeCrownToggle from './components/TreeCrownToggle';
import CarouselSlider from './components/singleImageSlider/CarouselSlider';
import MeasurementLayer from './components/layers/MeasurementLayer';
import MeasurementTools from './components/utilityButtons/measurementTools/MeasurementTools';
import MeasurementToggle from './components/utilityButtons/measurementTools/MeasurementToggle';
import DetectionProgressModal from '@components/modal/detectionProgressModal/DetectionProgressModal';
import { useObjectPickerStore } from '@stores/useObjectPickerStore';
import { useAnnotationSessionStore } from '@stores/useAnnotationSessionStore';
import CollectionPicker from '@routes/userRoutes/projects/singleProject/components/toolBar/collectionPicker/CollectionPicker';

const MapEditorWrapper = styled.div`
	position: relative;
	overflow: hidden;
	height: ${props => props.theme.utils.fullHeight};
`;

const InfoText = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	height: 70vh;
	color: #fff;
`;

const Loader = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 80vh;
	width: 100%;
	color: ${props => props.theme.colors.textColor};
`;

const BannerStack = styled.div`
	position: fixed;
	left: 376px;
	right: 167px;
	top: 78px;
	z-index: 2;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;
	pointer-events: none;
`;

const DemoBanner = styled.div`
	background-color: rgba(${props => props.theme.colors.backgroundRgb}, 0.92);
	color: ${props => props.theme.colors.textColor};
	padding: 12px 16px;
	border-radius: 4px;
	border-left: 3px solid ${props => props.theme.colors.green};
	pointer-events: auto;
	p {
		margin-bottom: 0;
		font-size: 0.85rem;
		line-height: 1.4;
	}
`;

const PlaceholderBanner = styled(DemoBanner)`
	span.progress-text {
		font-weight: 500;
		color: ${props => props.theme.colors.green};
	}
`;

const StyledMeasurementTools = styled(MeasurementTools)`
	${props =>
		props.$hasSibling &&
		`
	border-right: 1px solid hsl(0, 0%, 30%);
	margin-right: 1rem;
	padding-right: 1rem;
	border-radius: 0;`}

	/* Hovered Radio Button */
	.btn:hover {
		background: var(--bs-btn-hover-bg);
	}
`;

/**
 * MapEditor is the main component for the map editor page. It is a central hub for all the other components in the map editor page
 * and is responsible for:
 *
 * setting the context project context
 * getting data from the backend
 * contains all state variables for the map editor page
 */

const MapEditor = () => {
	const {
		pickedTask,
		isDemo,
		projectMode,
		annotationMode,
		measurementMode,
		mapObject,
		project,
		dispatch,
	} = useProject();
	const { roleAdmin, tierPro, subscriptionActive } = useAuth();
	const projectViewMode = useProjectMode();
	// Named as the measurement owner so the mode does not hide its own sketches.
	// Asking here rather than re-deriving keeps this in step with tools added later.
	const { busy: mapBusy } = useMapToolBusy({ owner: 'measurement' });

	const isSingleImageMode = projectMode === PROJECT_MODE.SINGLE_IMAGE;
	// The layers sidebar owns the Images layer of a single-image project and removes it
	// when it unmounts. An object annotation keeps it mounted (hidden), so the images
	// stay on the map while the area to annotate is drawn.
	const keepLayersSideBar =
		isSingleImageMode && annotationMode?.target === ANNOTATION_TARGET.OBJECT;
	const annotationAllowed = roleAdmin ? pickedTask : pickedTask && !pickedTask.basemodel;

	const { project_uuid } = useParams();
	const [projectLoading, setProjectLoading] = useState(true);
	const [errorMessage, setErrorMessage] = useState(null);
	const [infoMessage, setInfoMessage] = useState(null);
	const [isPlaceholder, setIsPlaceholder] = useState(false);

	const prevPlaceholderRef = useRef(false);

	const setActiveJobUuid = useObjectPickerStore(s => s.setActiveJobUuid);
	const setActiveJobProgressModal = useObjectPickerStore(s => s.setActiveJobProgressModal);
	const resetAnnotationSession = useAnnotationSessionStore(s => s.reset);

	const projectQuery = useQuery({
		queryKey: ['project', project_uuid],
		queryFn: async () => await getProject(project_uuid),
		enabled: !!project_uuid,
		retry: false,
		refetchInterval: isPlaceholder ? 15000 : false,
	});

	useEffect(() => {
		if (projectQuery?.data) {
			const { deleted: isDeleted } = projectQuery.data;
			if (isDeleted && !roleAdmin) {
				setInfoMessage({
					header: 'Deleted project',
					body: `Project with ID ${project_uuid} is deleted and can't be accessed.`,
				});
				setProjectLoading(false);
				return;
			}

			dispatch({ type: 'setProject', payload: projectQuery.data });
			dispatch({
				type: 'setIsDemo',
				payload: projectQuery.data.demo_project && !roleAdmin,
			});

			// If tierPro and not subscriptionactive and not demo then redirect to home
			if (tierPro && !subscriptionActive && !projectQuery.data.demo_project) {
				setErrorMessage('You need an active subscription to view this project');
				setProjectLoading(false);
				dispatch({ type: 'setProject', payload: null });
				return;
			}

			const center = projectQuery.data.center_coordinate;
			const e = projectQuery.data.extent;
			if (projectQuery.data.orthophoto_progress?.error) {
				setInfoMessage({
					header: 'Something seems to have gone wrong',
					body: (
						<>
							<a
								href={`mailto:post@biodrone.no?subject=Failing project with id ${projectQuery.data.uuid}`}>
								Please contact us at post@biodrone.no
							</a>{' '}
							and we will help you
						</>
					),
				});
			} else if (!center || (center[0] === -1 && center[1] === -1)) {
				// if projectQuery.data.created_at is more than 12 hours ago, then the project is not ready
				const created_at = new Date(projectQuery.data.created_at);
				const now = new Date();
				const diff = now - created_at;
				const hours = Math.floor(diff / 1000 / 60 / 60);

				if (hours > 12) {
					setInfoMessage({
						header: 'Creating a project sometimes take a while',
						body: (
							<>
								Especially when dealing with large datasets or if an issue arises.{' '}
								<br />
								<a
									href={`mailto:post@biodrone.no?subject=Question about project with id ${projectQuery.data.uuid}`}>
									Please contact us at post@biodrone.no
								</a>{' '}
								if you have any questions.
							</>
						),
					});
				} else {
					setInfoMessage({
						header: 'Project not ready',
						body: 'The map is not ready yet. This can take some time. Check again later.',
					});
				}
			} else {
				dispatch({ type: 'setExtent', payload: e });
			}

			// Track placeholder state (skip if there's an error to avoid polling forever)
			if (!projectQuery.data.orthophoto_progress?.error) {
				const placeholder = !!projectQuery.data.orthophoto_is_placeholder;
				setIsPlaceholder(placeholder);
			} else {
				setIsPlaceholder(false);
			}

			setProjectLoading(false);

			return;
		}

		if (projectQuery?.isError) {
			console.warn('Could not fetch and set project.', projectQuery.error);
			if (projectQuery?.error?.response?.status === 401) {
				setErrorMessage(`You are not authorized to view project ID ${project_uuid}`);
			} else {
				setErrorMessage(`Can't find project with ID ${project_uuid}`);
			}
			dispatch({ type: 'setProject', payload: null });
			dispatch({ type: 'setIsDemo', payload: false });

			setProjectLoading(false);
		}
	}, [projectQuery?.data, projectQuery?.isError]);

	// Cache-bust orthophoto tiles when placeholder flips to real
	useEffect(() => {
		if (prevPlaceholderRef.current && !isPlaceholder) {
			if (!mapObject) return; // map not mounted yet
			const orthoLayer = getLayerByCustomId(mapObject, 'orthophoto');
			if (!orthoLayer) return; // layer not attached yet
			const source = orthoLayer.getSource();
			const tilesPath =
				project?.map_layers?.find(l => l.layer === 'orthophoto')?.tiles_path ??
				project?.uuid + '/orthophoto_tiles';
			const cacheBust = new Date(project?.updated_at).getTime();
			const newUrl = buildTileUrl(tilesPath, { cacheBust });
			source.setUrl(newUrl);
			source.refresh();
		}
		prevPlaceholderRef.current = isPlaceholder;
	}, [isPlaceholder, mapObject, project]);

	useEffect(() => {
		return () => {
			// Reset states when unmounting
			dispatch({ type: 'resetState' });
			setActiveJobProgressModal(false);
			setActiveJobUuid(null);
			// Saved boxes live in local state for the project page only.
			resetAnnotationSession();
		};
	}, []);

	if (projectLoading)
		return (
			<Loader>
				<Spinner animation="border" role="status">
					<span className="visually-hidden">Loading page</span>
				</Spinner>
			</Loader>
		);

	if (errorMessage)
		return (
			<InfoText>
				<div>
					<h1>Error</h1>
					<p>{errorMessage}</p>
				</div>
			</InfoText>
		);

	return (
		<MapEditorWrapper>
			{infoMessage ? (
				<InfoText>
					<div>
						<h1>{infoMessage.header}</h1>
						<p>{infoMessage.body}</p>
					</div>
				</InfoText>
			) : (
				<>
					{(isDemo || isPlaceholder) && (
						<BannerStack>
							{isDemo && (
								<DemoBanner>
									<p>
										This is a demo project showcasing the results of an
										analysis. Other features are disabled.{' '}
										<Link to="/" className="inherit">
											Subscribe to create and analyze your own projects.
										</Link>
									</p>
								</DemoBanner>
							)}

							{isPlaceholder && (
								<PlaceholderBanner>
									<p>
										Quick preview — positioning is approximate. Full-resolution
										map will appear when processing completes.
										{projectQuery.data?.orthophoto_progress?.progress !=
											null && (
											<>
												{' '}
												<span className="progress-text">
													{Math.round(
														projectQuery.data.orthophoto_progress
															.progress
													)}
													%
												</span>
											</>
										)}
									</p>
								</PlaceholderBanner>
							)}
						</BannerStack>
					)}

					<Dialogue />
					<ConfirmModal />
					<ObjectPickerModal />
					<AoiPreviewLayer />
					<DetectionProgressModal />

					<MapToolbar>
						{projectViewMode === PROJECT_VIEW_MODE.MODEL && <ModelPicker />}

						{projectViewMode === PROJECT_VIEW_MODE.COLLECTION && (
							<Flex
								align="center"
								gap={8}
								style={{ marginRight: 'auto', padding: '8px 0' }}>
								<CollectionPicker />
								{/* CollectionPicker stays mounted in annotation mode (it renders
								    null itself, but its URL-to-context sync must keep running), so
								    the separator is hidden here rather than inside it. */}
								{!annotationMode && (
									<Divider orientation="vertical" style={{ height: 24 }} />
								)}
								{/* Detection and annotation exclude each other; only the annotation
								    entry stays, as its own Cancel/Draw boundary while in the flow. */}
								{!annotationMode && <ObjectPicker />}
								<AnnotateObjects />
								{/* The tour of this view belongs with this view's controls; the
								    separator keeps it from reading as part of AI Detection. */}
								{!annotationMode && (
									<Divider orientation="vertical" style={{ height: 24 }} />
								)}
								<ProjectTour tourId={TOUR_ID.COLLECTION_VIEW} />
							</Flex>
						)}

						{!annotationMode && (
							<>
								{measurementMode && (
									<StyledMeasurementTools $hasSibling={!!pickedTask} />
								)}

								<DetectInArea isTrained={pickedTask?.is_trained} />

								{/* Find (findSimilar/FindSimilar) is hidden, so the page calls none of
								    its /find/ and /similarity/ endpoints. Mount it here to bring it back. */}
							</>
						)}

						{annotationAllowed && <EditDetections />}
						{annotationAllowed && <Annotate />}
					</MapToolbar>

					{/*
					 * Bottom-right map controls. The rail is kept mounted while hidden,
					 * because BaseMapSelector owns the base map tile layer and unmounting
					 * would tear it down. Anything in here owning no such resource opts
					 * out and unmounts instead.
					 */}
					<MapControlsCluster isVisible={!annotationMode} aside={<MapScaleBar />}>
						{/* Owns only a query and a visibility poll, so it unmounts rather
						    than polling behind the hidden cluster all session. */}
						{!annotationMode && <CHMColorBar />}

						<MeasurementToggle />

						<BaseMapSelector />
					</MapControlsCluster>

					{/*
					 * Deliberately outside the annotation-mode branch below: unmounting
					 * discards every sketch, and Train AI stays reachable while measuring
					 * (it gates on `toolBarVisible`, not on map ownership). Hidden instead,
					 * so MeasurementToggle remains the only thing that discards sketches,
					 * and the only thing that asks first.
					 */}
					{measurementMode && <MeasurementLayer visible={!annotationMode && !mapBusy} />}

					{(!annotationMode || keepLayersSideBar) && (
						<div style={{ display: annotationMode ? 'none' : 'contents' }}>
							<LayersSideBar />
						</div>
					)}

					{!annotationMode ? (
						<>
							<MapLegend />

							{/*<TreeCrownToggle />*/}

							{isSingleImageMode && <CarouselSlider />}
						</>
					) : (
						<AnnotationSidebar />
					)}

					<MapView />
				</>
			)}
		</MapEditorWrapper>
	);
};

export default MapEditor;
